/**
 * AdminUserDataPanel - Manage badges, achievements, streaks for users
 * Used inside AdminAnalytics page
 */
import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Search, Trophy, Flame, RefreshCw, Plus, Trash2, Save, Award, 
  User, Shield, Loader2, CheckCircle, AlertTriangle, Zap
} from "lucide-react";

const BADGE_PRESETS = [
  { id: "first_country", name: "First Country", tier: "bronze" },
  { id: "five_countries", name: "5 Countries", tier: "silver" },
  { id: "ten_countries", name: "10 Countries", tier: "gold" },
  { id: "twentyfive_countries", name: "25 Countries", tier: "platinum" },
  { id: "fifty_countries", name: "50 Countries", tier: "diamond" },
  { id: "first_item", name: "First Item", tier: "bronze" },
  { id: "five_items", name: "5 Items", tier: "silver" },
  { id: "ten_items", name: "10 Items", tier: "gold" },
  { id: "twentyfive_items", name: "25 Items", tier: "platinum" },
  { id: "fifty_items", name: "50 Items", tier: "diamond" },
  { id: "streak_3", name: "3-Day Streak", tier: "bronze" },
  { id: "streak_7", name: "7-Day Streak", tier: "silver" },
  { id: "streak_14", name: "14-Day Streak", tier: "gold" },
  { id: "streak_30", name: "30-Day Streak", tier: "platinum" },
  { id: "partner_linked", name: "Partner Linked", tier: "silver" },
  { id: "beach_lover", name: "Beach Lover", tier: "gold" },
  { id: "premium_explorer", name: "Premium Explorer", tier: "gold" },
];

const TIER_COLORS = {
  bronze: "text-amber-600 bg-amber-100 dark:bg-amber-900/30",
  silver: "text-gray-500 bg-gray-100 dark:bg-gray-800/50",
  gold: "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30",
  platinum: "text-cyan-600 bg-cyan-100 dark:bg-cyan-900/30",
  diamond: "text-violet-600 bg-violet-100 dark:bg-violet-900/30",
};

export default function AdminUserDataPanel() {
  const [searchEmail, setSearchEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userId, setUserId] = useState(null);

  // Streak editing
  const [editStreak, setEditStreak] = useState(null);
  const [savingStreak, setSavingStreak] = useState(false);

  // Badge granting
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantBadge, setGrantBadge] = useState({ badge_id: "", badge_name: "", tier: "gold" });
  const [granting, setGranting] = useState(false);

  // Recalculating
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  const searchUser = async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    setUserData(null);
    setRecalcResult(null);
    try {
      // First find user by email
      const usersRes = await axios.get(`${API}/admin/users?search=${encodeURIComponent(searchEmail.trim())}`);
      const users = usersRes.data.users || usersRes.data;
      if (!users || users.length === 0) {
        toast.error("User not found");
        setSearching(false);
        return;
      }
      const foundUser = users[0];
      setUserId(foundUser.id);

      // Fetch their badges/achievements
      const dataRes = await axios.get(`${API}/admin/user/${foundUser.id}/badges-achievements`);
      setUserData(dataRes.data);
      setEditStreak({
        current_streak: dataRes.data.streak?.current_streak || 0,
        longest_streak: dataRes.data.streak?.longest_streak || 0,
        total_visits: dataRes.data.streak?.total_visits || 0,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to find user");
    } finally {
      setSearching(false);
    }
  };

  const handleRecalculate = async () => {
    if (!userId) return;
    setRecalculating(true);
    setRecalcResult(null);
    try {
      const res = await axios.post(`${API}/admin/user/${userId}/recalculate-achievements`);
      setRecalcResult(res.data);
      toast.success(`Recalculated! ${res.data.new_achievements_granted} new badges awarded`);
      // Refresh data
      const dataRes = await axios.get(`${API}/admin/user/${userId}/badges-achievements`);
      setUserData(dataRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Recalculation failed");
    } finally {
      setRecalculating(false);
    }
  };

  const handleGrantBadge = async () => {
    if (!grantBadge.badge_id) { toast.error("Badge ID required"); return; }
    setGranting(true);
    try {
      await axios.post(`${API}/admin/user/${userId}/grant-achievement`, grantBadge);
      toast.success(`Badge "${grantBadge.badge_name || grantBadge.badge_id}" granted!`);
      setShowGrantModal(false);
      setGrantBadge({ badge_id: "", badge_name: "", tier: "gold" });
      // Refresh
      const dataRes = await axios.get(`${API}/admin/user/${userId}/badges-achievements`);
      setUserData(dataRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant badge");
    } finally {
      setGranting(false);
    }
  };

  const handleRemoveBadge = async (badgeId) => {
    try {
      await axios.delete(`${API}/admin/user/${userId}/achievement/${badgeId}`);
      toast.success("Badge removed");
      const dataRes = await axios.get(`${API}/admin/user/${userId}/badges-achievements`);
      setUserData(dataRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove badge");
    }
  };

  const handleSaveStreak = async () => {
    if (!editStreak) return;
    setSavingStreak(true);
    try {
      await axios.put(`${API}/admin/user/${userId}/streak`, editStreak);
      toast.success("Streak updated!");
      const dataRes = await axios.get(`${API}/admin/user/${userId}/badges-achievements`);
      setUserData(dataRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update streak");
    } finally {
      setSavingStreak(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            User Data Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Search for a user by email to manage their badges, achievements, and streaks.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); searchUser(); }} className="flex gap-2">
            <Input
              placeholder="User email (e.g. patricia.87@hotmail.co.uk)"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="flex-1"
              data-testid="admin-user-search-input"
            />
            <Button type="submit" disabled={searching} data-testid="admin-user-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* User Data */}
      {userData && (
        <>
          {/* User Info */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{userData.user.name}</p>
                  <p className="text-sm text-muted-foreground">{userData.user.email}</p>
                </div>
                <div className="ml-auto flex gap-4 text-center">
                  <div>
                    <p className="text-lg font-bold">{userData.stats.total_items}</p>
                    <p className="text-xs text-muted-foreground">Items</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{userData.stats.completed_items}</p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{userData.stats.countries_visited}</p>
                    <p className="text-xs text-muted-foreground">Countries</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions Row */}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleRecalculate} disabled={recalculating} variant="outline" data-testid="admin-recalculate-btn">
              {recalculating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Recalculate Achievements
            </Button>
            <Button onClick={() => setShowGrantModal(true)} data-testid="admin-grant-badge-btn">
              <Plus className="w-4 h-4 mr-1" />
              Grant Badge
            </Button>
          </div>

          {/* Recalculation Result */}
          {recalcResult && (
            <Card className="border-green-500/30 bg-green-500/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Recalculation Complete</p>
                    <p className="text-sm text-muted-foreground">
                      Stats: {recalcResult.stats.countries} countries, {recalcResult.stats.completed} completed items
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Existing: {recalcResult.existing_achievements} | New: {recalcResult.new_achievements_granted}
                    </p>
                    {recalcResult.granted?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {recalcResult.granted.map(g => (
                          <div key={g.id} className="text-sm flex items-center gap-1">
                            <Award className="w-3 h-3 text-primary" />
                            <span>{g.badge_name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${TIER_COLORS[g.tier] || ''}`}>{g.tier}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Achievements */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Achievements ({userData.achievements.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {userData.achievements.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No achievements yet</p>
                  <p className="text-xs mt-1">Click "Recalculate Achievements" to auto-detect missing badges</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {userData.achievements.map(ach => (
                    <div key={ach.id || ach.badge_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30" data-testid={`achievement-${ach.badge_id}`}>
                      <Award className="w-5 h-5 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-foreground truncate">{ach.badge_name || ach.badge_id}</p>
                        <p className="text-xs text-muted-foreground">
                          {ach.unlocked_at ? new Date(ach.unlocked_at).toLocaleDateString() : '—'}
                          {ach.granted_by_admin && <span className="ml-1 text-amber-500">(admin)</span>}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${TIER_COLORS[ach.tier] || ''}`}>{ach.tier}</span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => handleRemoveBadge(ach.badge_id)} data-testid={`remove-badge-${ach.badge_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Streak Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                Streak Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editStreak && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Current Streak</label>
                      <Input
                        type="number"
                        min="0"
                        value={editStreak.current_streak}
                        onChange={(e) => setEditStreak(prev => ({ ...prev, current_streak: parseInt(e.target.value) || 0 }))}
                        data-testid="streak-current-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Longest Streak</label>
                      <Input
                        type="number"
                        min="0"
                        value={editStreak.longest_streak}
                        onChange={(e) => setEditStreak(prev => ({ ...prev, longest_streak: parseInt(e.target.value) || 0 }))}
                        data-testid="streak-longest-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Total Visits</label>
                      <Input
                        type="number"
                        min="0"
                        value={editStreak.total_visits}
                        onChange={(e) => setEditStreak(prev => ({ ...prev, total_visits: parseInt(e.target.value) || 0 }))}
                        data-testid="streak-visits-input"
                      />
                    </div>
                  </div>
                  <Button onClick={handleSaveStreak} disabled={savingStreak} data-testid="save-streak-btn">
                    {savingStreak ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save Streak
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Grant Badge Modal */}
      <Dialog open={showGrantModal} onOpenChange={setShowGrantModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Grant Badge
            </DialogTitle>
            <DialogDescription>Manually award a badge to {userData?.user?.name}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Preset badges */}
            <div>
              <label className="text-xs text-muted-foreground block mb-2">Quick Select</label>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                {BADGE_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => setGrantBadge({ badge_id: preset.id, badge_name: preset.name, tier: preset.tier })}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      grantBadge.badge_id === preset.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom badge */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground block">Or custom badge</label>
              <Input
                placeholder="Badge ID (e.g. custom_badge)"
                value={grantBadge.badge_id}
                onChange={(e) => setGrantBadge(prev => ({ ...prev, badge_id: e.target.value }))}
              />
              <Input
                placeholder="Badge Name"
                value={grantBadge.badge_name}
                onChange={(e) => setGrantBadge(prev => ({ ...prev, badge_name: e.target.value }))}
              />
              <select
                value={grantBadge.tier}
                onChange={(e) => setGrantBadge(prev => ({ ...prev, tier: e.target.value }))}
                className="w-full p-2 rounded-lg bg-background border border-input text-sm"
              >
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
                <option value="diamond">Diamond</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrantModal(false)}>Cancel</Button>
            <Button onClick={handleGrantBadge} disabled={granting || !grantBadge.badge_id}>
              {granting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Award className="w-4 h-4 mr-1" />}
              Grant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
